import React, { useContext, useEffect, useState } from 'react';

import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckIcon from '@material-ui/icons/CheckCircleRounded';
import ErrorIcon from '@material-ui/icons/ErrorRounded';
import NextIcon from '@material-ui/icons/NavigateNextRounded';
import { navigate, RouteComponentProps } from '@reach/router';
import SwipeableViews from 'react-swipeable-views';

import { GlobalContext } from '../../../../store';
import { IGame, IGroupEnrollment } from '../../../../store/player/games/store';

import Button from '../../../../components/Button';
import PlayerSubHeader from '../../../../components/PlayerSubHeader';
import RoundScoreSummary from '../../../../components/RoundScoreSummary/RoundScoreSummary';
import useGetHoleRange from '../Utils/getHoleRange';
import { Stats } from '../Components/Stats';
import Scorer from '../Components/Scorer';
import { TabPanel } from './TabPanel';
import { WarningMessage } from './WarningMessage';

interface IProps extends RouteComponentProps {
  eventId: number;
  roundId: number;
}

interface IConfirmation {
  [playerId: number]: boolean;
}

const ConfirmScores = (props: IProps) => {
  const { roundId, eventId } = props;

  const {
    actions,
    actions: {
      notifications: { push: pushNotifications }
    },
    games,
    scores,
    user
  } = useContext(GlobalContext);

  const game: IGame = Object.values(games).find(g => g.round.id === Number(roundId));
  const [editMode, setEditMode] = useState(false);
  {
    /* We're using this to hide some parts of this component the day before UGP's big event as the complete screen is not working properly */
  }
  const [temporalHide] = useState(false);
  const [player, setPlayer] = useState<IGroupEnrollment>();
  const groupScores = game && scores[game.group.id];
  const selectedScores = groupScores && player && groupScores.find(s => s.enrollment === player.id);

  const [confirmed, setConfirmed] = useState<IConfirmation>({});
  const holesRange = useGetHoleRange(eventId, roundId);
  const isOfficialScorer = game && user.id === game.group.scorer;

  useEffect(() => {
    if (game) {
      actions.scores.get(game.group.id);
    }
  }, [game]);

  const toggleConfirm = (playerId: number) => () => {
    setConfirmed({
      ...confirmed,
      [playerId]: !confirmed[playerId]
    });
  };

  const viewDetails = (e: any) => () => {
    setPlayer(e);
    setEditMode(true);
  };

  const closeDetails = () => {
    setEditMode(false);
  };

  const getScores = (enrollmentId: number) => {
    const scorer = (groupScores || []).find(s => s.enrollment === enrollmentId);

    const score = (scorer ? scorer.scores : []).reduce(
      (acc, s) => ({
        gross: acc.gross + s.gross,
        net: acc.net + s.net
      }),
      { gross: 0, net: 0 }
    );

    return score;
  };

  const roundDone = groupScores && groupScores[0].scores.length === 18;

  const confirmGame = () => {
    if (game.status !== 'confirmed') {
      actions.games.updateGame(game.id, { status: 'confirmed' }).catch();

      pushNotifications({
        status: 'unread',
        text: 'Scores successfully submitted',
        type: 'success'
      });

      navigate(`/event/${eventId}/leaderboard`);
    }
  };

  const canSubmit =
    game &&
    game.group.enrollments.length ===
      Object.values(confirmed).reduce((count, v) => count + Number(v), 0);

  const canConfirm = roundDone && confirmed[player ? player.id : 0];

  return (
    <div className="score-route confirm-scores has-header">
      <PlayerSubHeader>
        <h2 className="confirmation-title">
          <div className="centered">COMPLETE ROUND</div>
        </h2>

        <p className="confirmation-subtitle">
          To submit final scores, click on each player below and confirm their scores
        </p>
      </PlayerSubHeader>

      {!editMode && (
        <div className="title-confirmation-container">
          <div className="background" />

          <div className="title-confirmation">
            {temporalHide && (
              <>
                <p>SCORES</p>

                <div className="score-types">
                  <div>
                    <div>GROSS</div>

                    <div>NET</div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <SwipeableViews index={Number(editMode)} dir={!editMode ? 'x' : 'x-reverse'} disabled={true}>
        <TabPanel value={Number(editMode)} index={0} dir={'x'}>
          <div className="scorers padding">
            {game &&
              game.group.enrollments.map(e => {
                const score = getScores(e.id);

                return (
                  <Scorer item={e} key={`scorer-${e.id}`} onClick={viewDetails(e)}>
                    {temporalHide && <Stats gross={score.gross} net={score.net} />}

                    {!roundDone && <ErrorIcon color="secondary" className="warning caret" />}

                    {roundDone && !confirmed[e.id] && (
                      <NextIcon color="primary" className="caret" />
                    )}

                    {roundDone && confirmed[e.id] && (
                      <CheckIcon color="secondary" className="caret" />
                    )}
                  </Scorer>
                );
              })}
          </div>
        </TabPanel>

        <TabPanel value={Number(editMode)} index={1} dir={'x'} className="pull-up">
          {!roundDone && <WarningMessage>Missing Scores</WarningMessage>}

          {player && temporalHide && (
            <RoundScoreSummary
              item={player}
              playerScores={selectedScores}
              onClose={closeDetails}
              showWarning={!roundDone}
              range={holesRange}
              showPlayerDescription={true}
              fullTable={false}
              hideConfirmedSignature={true}
              hideIndexRow={true}
            />
          )}

          {isOfficialScorer && (
            <div className="confirm-box padding">
              <FormControlLabel
                classes={{ label: 'checkbox-label-summary' }}
                control={
                  <Checkbox
                    checkedIcon={<CheckBoxIcon className="form-icon override" />}
                    color="primary"
                    icon={<CheckBoxOutlineBlankIcon className="form-icon override" />}
                    id="accepted-tos"
                    onChange={toggleConfirm(player ? player.id : 0)}
                    checked={!!confirmed[player ? player.id : 0]}
                    value="on"
                    disabled={!roundDone}
                    classes={{ disabled: 'disabled-checkbox' }}
                  />
                }
                label={<>CHECK TO CONFIRM SCORES</>}
              />
            </div>
          )}
        </TabPanel>
      </SwipeableViews>

      <Button
        variant="contained"
        color="primary"
        className="save-scorer-button"
        onClick={closeDetails}
      >
        Back
      </Button>

      <div className="padding">
        {isOfficialScorer && (
          <Button
            variant="contained"
            color="primary"
            className="save-scorer-button"
            disabled={editMode ? !canConfirm : !canSubmit}
            onClick={editMode ? closeDetails : confirmGame}
          >
            {editMode ? 'CONFIRM SCORES' : 'SUBMIT SCORES'}
          </Button>
        )}
      </div>
    </div>
  );
};

export { ConfirmScores };
